import { useEffect, useState } from "react";
import { activeExecutionLabel } from "../lib/delegateActivity";
import type { DelegateStatus } from "../lib/types";

interface Props {
  astra?: DelegateStatus;
  claudeName?: "Claude" | "Opus";
  requestCreatedAt?: string;
  terminal?: string;
  preparing?: boolean;
  working?: boolean;
}
// Original AssistantBubble.tsx, a81681ee95a04b6eb6378a42603185518eb5d97d.
const ACTIVITY_WORDS = ["Thinking", "Reading", "Searching", "Synthesizing", "Drafting", "Cross-checking", "Refining"];

/** One current-task footer. Wall time never claims provider thinking time. */
export default function RequestProgressFooter({ astra, claudeName, requestCreatedAt, terminal, preparing=false, working=false }: Props) {
  const active = !!astra || !!claudeName || preparing || working;
  const [now, setNow] = useState(() => Date.now());
  const [word,setWord] = useState(0);
  const [fading,setFading] = useState(false);
  const phase = preparing ? "Starting" : astra?.status === "waiting" ? "Queued" : astra?.status === "verifying" ? "Checking" : "Working";
  const [previousPhase,setPreviousPhase] = useState(phase);
  if (previousPhase !== phase) { setPreviousPhase(phase); if (fading) setFading(false); }
  const requestAt = typeof requestCreatedAt === "string" ? Date.parse(requestCreatedAt) : NaN;
  const nativeElapsed = astra?.status !== "waiting" ? activeExecutionLabel(astra?.executionElapsedMs) : null;
  const hasRequestTime = Number.isFinite(requestAt) && requestAt <= now;
  // Keep per-second updates out of the archive/segmentation component.
  useEffect(() => {
    if (!active || nativeElapsed !== null || !hasRequestTime) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active, nativeElapsed, hasRequestTime, requestCreatedAt]);
  useEffect(()=>{
    if (!active || phase !== "Working") return;
    const motion=window.matchMedia("(prefers-reduced-motion: reduce)");
    let timer:number|undefined,fadeTimer:number|undefined;
    const update=()=>{
      if(timer !== undefined) window.clearInterval(timer);
      if(fadeTimer !== undefined)window.clearTimeout(fadeTimer);
      timer=motion.matches ? undefined : window.setInterval(()=>{
        setFading(true);
        fadeTimer=window.setTimeout(()=>{setWord(value=>(value+1)%ACTIVITY_WORDS.length);setFading(false);},220);
      },2400);
    };
    const preferences=()=>{setFading(false);update();};
    update();motion.addEventListener("change",preferences);
    return ()=>{if(timer !== undefined)window.clearInterval(timer);if(fadeTimer !== undefined)window.clearTimeout(fadeTimer);motion.removeEventListener("change",preferences);};
  },[active,phase]);
  if (!active && !terminal) return null;
  const elapsed = nativeElapsed ?? (active && hasRequestTime
    ? activeExecutionLabel(Math.max(0, now - requestAt)) : null);
  const attempt = Number.isInteger(astra?.executionAttempt) && Number(astra?.executionAttempt) > 1
    ? `Attempt ${astra!.executionAttempt} execution` : "Execution";
  const timeLabel = nativeElapsed !== null ? attempt : "Task elapsed";
  const timeTitle = nativeElapsed !== null
    ? "Measured native execution wall time; includes tools, excludes admission wait. Latest attempt only."
    : "Time since this exact request was sent; includes queueing, tools and provider handoffs. Not model thinking time.";
  return <footer className="delegateProgress__footer" aria-live="off">
    <span className="msg__progress requestProgress__indicator" title={phase==="Working" ? "Working indicator; rotating words are an animation, not a live tool trace." : undefined}>
      <span className={"delegateProgress__activityWord"+(fading?" is-fading":"")} aria-label={phase}>
        {/* Keyed by phase: Starting → Working (→ Checking) slides in like the rotating words. */}
        <span aria-hidden="true" key={active ? phase : "done"} className="wordIn">{active ? (phase==="Working" ? ACTIVITY_WORDS[word] : phase)+"…" : terminal}</span>
      </span>
      {active && elapsed && <span className="msg__progressElapsed delegateProgress__elapsed" title={timeTitle} aria-label={timeLabel+": "+elapsed}> · {elapsed}</span>}
    </span>
  </footer>;
}
